import { jsPDF } from "jspdf";
import { toPng } from "html-to-image";

/**
 * The board pack as a PDF, built from the pack exactly as it is on screen.
 *
 * Each report is captured as rendered, so the PDF shows what the reader was
 * looking at when they clicked: the same fiscal year, team, figures and chart
 * views. The pack is laid out at a fixed width first, so a report captured on
 * a wide monitor is not shrunk to unreadable text on the page.
 *
 * A4 landscape: the retention and NRR tables are seven columns wide and read
 * better across the page than down it.
 */

export const CAPTURE_WIDTH_PX = 1120;

const PAGE = { width: 297, height: 210 };
const MARGIN = { top: 16, right: 14, bottom: 14, left: 14 };
const CONTENT_WIDTH = PAGE.width - MARGIN.left - MARGIN.right;
const CONTENT_BOTTOM = PAGE.height - MARGIN.bottom;
const GAP = 6;

// Rare Technology's design system: slate text, mint accent, gold band.
const INK = [50, 60, 68];        // slate 800
const MUTED = [125, 139, 150];   // slate 500
const ACCENT = [17, 153, 150];   // teal deep, for accent text on white
const MINT = [80, 227, 194];
const GOLD = [207, 167, 123];
const SLATE = [50, 60, 68];
const RULE = [221, 221, 221];

// What the pack calls itself, on the cover, each page and the file name.
const DEFAULT_PACK_NAME = "Strategic Development Dashboard";

/** An SVG from /public as a PNG, for jsPDF, which cannot draw SVG itself. */
async function svgToPng(url, width = 600) {
  const text = await (await fetch(url)).text();
  const blob = new Blob([text], { type: "image/svg+xml" });
  const objectUrl = URL.createObjectURL(blob);
  try {
    const image = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = objectUrl;
    });
    const ratio = image.naturalHeight / image.naturalWidth || 0.47;
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = Math.round(width * ratio);
    canvas.getContext("2d").drawImage(image, 0, 0, canvas.width, canvas.height);
    return { dataUrl: canvas.toDataURL("image/png"), ratio };
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Controls that exist to be clicked (edit, view toggles) are not part of the
// report and are left out of the capture.
const keepNode = (node) =>
  !(node instanceof Element && node.classList && node.classList.contains("print:hidden"));

async function capture(element) {
  const dataUrl = await toPng(element, {
    pixelRatio: 2,
    backgroundColor: "#ffffff",
    cacheBust: true,
    filter: keepNode,
  });
  const { width, height } = element.getBoundingClientRect();
  return { dataUrl, width, height };
}

function drawFooter(doc, text, pageNumber, pageCount) {
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.2);
  doc.line(MARGIN.left, PAGE.height - 9, PAGE.width - MARGIN.right, PAGE.height - 9);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text(text, MARGIN.left, PAGE.height - 5);
  doc.text(`${pageNumber} of ${pageCount}`, PAGE.width - MARGIN.right, PAGE.height - 5, { align: "right" });
}

function drawSectionHeading(doc, name, continued = false, packName = DEFAULT_PACK_NAME) {
  doc.setFillColor(...MINT);
  doc.rect(MARGIN.left, MARGIN.top - 10, 16, 1.4, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(8);
  doc.setTextColor(...ACCENT);
  doc.text(String(packName).toUpperCase(), MARGIN.left, MARGIN.top - 4);
  doc.setFontSize(16);
  doc.setTextColor(...INK);
  doc.text(continued ? `${name} (continued)` : name, MARGIN.left, MARGIN.top + 4);
  doc.setDrawColor(...RULE);
  doc.setLineWidth(0.3);
  doc.line(MARGIN.left, MARGIN.top + 7, PAGE.width - MARGIN.right, MARGIN.top + 7);
  return MARGIN.top + 12;
}

function drawCover(doc, meta, contents, logo) {
  // Slate panel with the logo and title, contents on white beside it.
  const panel = 128;
  doc.setFillColor(...SLATE);
  doc.rect(0, 0, panel, PAGE.height, "F");
  doc.setFillColor(...MINT);
  doc.rect(0, 0, panel, 3, "F");

  if (logo) {
    const width = 34;
    doc.addImage(logo.dataUrl, "PNG", 20, 24, width, width * logo.ratio, undefined, "FAST");
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...MINT);
  doc.text((meta.packName || DEFAULT_PACK_NAME).toUpperCase(), 20, 78, { charSpace: 0.8 });

  doc.setFontSize(26);
  doc.setTextColor(255, 255, 255);
  doc.text(doc.splitTextToSize(meta.portalName || DEFAULT_PACK_NAME, panel - 36), 20, 92);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);
  doc.setTextColor(207, 214, 219);
  const lines = [
    meta.fiscalYear ? `Fiscal year ${meta.fiscalYear}` : null,
    meta.team && meta.team !== "All" ? meta.team : null,
    meta.dateLabel,
  ].filter(Boolean);
  lines.forEach((line, index) => doc.text(line, 20, 116 + index * 7));

  // The gold band, Rare's one signature flat-colour block.
  doc.setFillColor(...GOLD);
  doc.rect(0, PAGE.height - 22, panel, 12, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7.5);
  doc.setTextColor(255, 255, 255);
  doc.text("DRIVING EQUALITY THROUGH TECHNOLOGY", panel / 2, PAGE.height - 14.6, {
    align: "center", charSpace: 0.6,
  });

  // Contents, with the page each section starts on.
  const left = panel + 20;
  const right = PAGE.width - 20;
  doc.setFont("helvetica", "bold");
  doc.setFontSize(9);
  doc.setTextColor(...ACCENT);
  doc.text("CONTENTS", left, 32, { charSpace: 0.8 });
  let y = 44;
  for (const section of contents) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(...INK);
    doc.text(section.name, left, y);
    doc.text(String(section.page), right, y, { align: "right" });
    y += 6;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(...MUTED);
    for (const report of section.reports) {
      if (y > PAGE.height - 20) break;
      doc.text(report.title, left + 4, y);
      doc.text(String(report.page), right, y, { align: "right" });
      y += 5;
    }
    y += 4;
  }
}

/**
 * Build and download the PDF.
 *
 * `root` is the element holding the pack; sections are marked
 * data-pack-section and reports data-pack-report inside them. The caller lays
 * the pack out at CAPTURE_WIDTH_PX before calling, and restores it after.
 */
export async function downloadBoardPackPdf(root, meta, onProgress = () => {}) {
  if (document.fonts?.ready) await document.fonts.ready;
  // Give responsive charts a moment to redraw at the capture width.
  await wait(600);

  const sections = Array.from(root.querySelectorAll("[data-pack-section]"));
  const total = sections.reduce(
    (sum, s) => sum + s.querySelectorAll("[data-pack-report]").length, 0,
  );

  const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4", compress: true });
  // Page 1 is the cover, drawn last once the contents' page numbers are known.
  const contents = [];
  let done = 0;

  for (const section of sections) {
    const name = section.getAttribute("data-pack-section");
    // A section whose reports were all left out of this download gets no page.
    if (!section.querySelectorAll("[data-pack-report]").length) continue;
    doc.addPage();
    const packName = meta.packName || DEFAULT_PACK_NAME;
    let y = drawSectionHeading(doc, name, false, packName);
    const entry = { name, page: doc.getNumberOfPages(), reports: [] };

    for (const report of Array.from(section.querySelectorAll("[data-pack-report]"))) {
      onProgress(done, total);
      const image = await capture(report);
      done += 1;
      if (!image.width || !image.height) continue;

      let width = CONTENT_WIDTH;
      let height = (image.height / image.width) * width;
      const available = CONTENT_BOTTOM - (MARGIN.top + 12);
      // Taller than a whole page: shrink to fit rather than slicing a chart.
      if (height > available) {
        height = available;
        width = (image.width / image.height) * height;
      }
      if (y + height > CONTENT_BOTTOM) {
        doc.addPage();
        y = drawSectionHeading(doc, name, true, packName);
      }
      const x = MARGIN.left + (CONTENT_WIDTH - width) / 2;
      doc.addImage(image.dataUrl, "PNG", x, y, width, height, undefined, "FAST");
      entry.reports.push({
        title: report.getAttribute("data-pack-report"),
        page: doc.getNumberOfPages(),
      });
      y += height + GAP;
    }
    contents.push(entry);
  }
  onProgress(total, total);

  let logo = null;
  try {
    logo = await svgToPng("/brand/rare-logo-white.svg");
  } catch {
    logo = null; // the cover still works without it
  }
  doc.setPage(1);
  drawCover(doc, meta, contents, logo);

  const pageCount = doc.getNumberOfPages();
  const footer = [meta.portalName, meta.packName || DEFAULT_PACK_NAME, meta.fiscalYear ? `FY ${meta.fiscalYear}` : null, meta.dateLabel]
    .filter(Boolean).join("  ·  ");
  for (let page = 2; page <= pageCount; page += 1) {
    doc.setPage(page);
    drawFooter(doc, footer, page, pageCount);
  }

  const safe = (text) => String(text || "").replace(/[\\/:*?"<>|]+/g, "-").trim();
  doc.save(
    `${safe(meta.portalName) || "Rare"} - ${safe(meta.packName) || DEFAULT_PACK_NAME} `
    + `${safe(meta.fiscalYear)} - ${meta.fileDate}.pdf`,
  );
}
